/**
 * Vuex store for the affiliate SPA.
 *
 * Persisted under localStorage key "webwalker" via secure-ls (AES).
 * VUE_APP_LS_ENCRYPTION_SECRET empty in committed production = weak obfuscation only;
 * rotating the secret forces all affiliates to re-login.
 */
import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import SecureLS from 'secure-ls';
import AuthApi from '@/api/AuthApi';
import SettingsApi from '@/api/SettingsApi';
import PlatformAccountsApi from '@/api/platform/AccountsApi';
import { resolveAccountContext } from '@/helpers/resolveAccountContext';

const ls = new SecureLS({
  encodingType: 'aes',
  isCompression: false,
  encryptionSecret: process.env.VUE_APP_LS_ENCRYPTION_SECRET,
});

const store = createStore({
  plugins: [
    createPersistedState({
      key: 'webwalker',
      storage: {
        getItem: (key) => {
          try {
            return ls.get(key);
          } catch (err) {
            ls.clear();
            return ls.get(key);
          }
        },
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
    }),
  ],
  state: {
    token: false,
    user: false,
    preferredAccountId: null,
    platformAccounts: [],
    isDarkMode: false,
    errors: [],
  },
  getters: {
    /** Presence of a token only — not a live server check. */
    authenticated(state) {
      return !!state.token;
    },
    isAdmin(state) {
      return !!(state.user && state.user.is_admin);
    },
    isPlatformAdmin(state) {
      return !!(state.user && state.user.is_platform_admin);
    },
    accounts(state) {
      if (!state.user || !Array.isArray(state.user.accounts)) {
        return [];
      }
      return state.user.accounts;
    },
    switchableAccounts(state, getters) {
      if (getters.isPlatformAdmin && state.platformAccounts.length) {
        return state.platformAccounts;
      }
      return getters.accounts;
    },
    currentAccount(state) {
      if (!state.user || !state.user.current_account) {
        return null;
      }
      return state.user.current_account;
    },
    currentAccountId(state) {
      if (!state.user || !state.user.current_account) {
        return null;
      }
      return state.user.current_account.id;
    },
    currentAccountRole(state) {
      if (!state.user || !state.user.current_account) {
        return null;
      }
      return state.user.current_account.role || null;
    },
  },
  mutations: {
    setToken(state, token) {
      state.token = token;
    },
    setUser(state, user) {
      if (!user) {
        state.user = user;
        return;
      }
      state.user = resolveAccountContext(
        user,
        state.preferredAccountId,
        state.platformAccounts,
      );
    },
    setPreferredAccountId(state, accountId) {
      state.preferredAccountId = accountId == null ? null : Number(accountId);
    },
    setPlatformAccounts(state, accounts) {
      state.platformAccounts = Array.isArray(accounts) ? accounts : [];
    },
    setDarkMode(state, isDarkMode) {
      state.isDarkMode = isDarkMode;
    },
    pushErrors(state, errors) {
      state.errors.push(errors);
    },
    clearErrors(state) {
      state.errors = [];
    },
  },
  actions: {
    loadUser({ commit, state }) {
      if (!state.token) {
        return Promise.resolve();
      }

      return AuthApi.auth()
        .then((response) => {
          const user = response.data.data;
          commit('setUser', user);
          if (user.settings) {
            commit('setDarkMode', user.settings.dark_mode);
          }
          if (!user.is_platform_admin) {
            commit('setPlatformAccounts', []);
            commit('setUser', user);
            return undefined;
          }
          return PlatformAccountsApi.list()
            .then((accountsResponse) => {
              const body = accountsResponse.data || {};
              commit('setPlatformAccounts', body.data || []);
              commit('setUser', user);
            })
            .catch(() => {
              commit('setPlatformAccounts', []);
              commit('setUser', user);
            });
        })
        .catch((error) => {
          // Drop auth via Vuex so persistence stays consistent (do not localStorage.clear).
          commit('setToken', false);
          commit('setUser', false);
          commit('setPreferredAccountId', null);
          commit('setPlatformAccounts', []);
          return Promise.reject(error);
        });
    },
    switchAccount({ commit, state, getters }, accountId) {
      const nextId = Number(accountId);
      if (!nextId || getters.currentAccountId === nextId) {
        return;
      }
      const match = getters.switchableAccounts
        .find((account) => Number(account.id) === nextId);
      if (!match) {
        return;
      }
      commit('setPreferredAccountId', nextId);
      if (state.user) {
        commit('setUser', { ...state.user });
      }
    },
    switchDarkMode({ commit }, isDarkMode) {
      SettingsApi.updateUser({
        settings: { dark_mode: isDarkMode },
      });
      commit('setDarkMode', isDarkMode);
    },
    pushErrors({ commit }, errors) {
      commit('pushErrors', errors);
    },
    clearErrors({ commit, state }) {
      if (state.errors.length) {
        commit('clearErrors');
      }
    },
  },
});

export default store;
